/**
 * CAR-IA UI Gallery (v3) — a living catalog of the design system: tokens,
 * type, buttons, chips, and marketplace components in every state. Dev/design
 * reference; routed at /ui-gallery. Keep this in sync as components land.
 */
import React from 'react';
import {
  TopBar,
  ColorRail,
  VehicleCard,
  TrustChip,
  DecisionChip,
  StatusChip,
  LeadChip,
  PremiumBadge,
  TasadorChip,
  PriceDropChip,
  SectionHeader,
  TrustMarkGrid,
  PriceBlock,
} from './index';
import './caria-components.css';

const SWATCHES: { group: string; items: { name: string; varName: string }[] }[] = [
  {
    group: 'Surfaces & Ink',
    items: [
      { name: 'surf-page', varName: '--surf-page' },
      { name: 'surf-card', varName: '--surf-card' },
      { name: 'surf-tint', varName: '--surf-tint' },
      { name: 'topbar', varName: '--topbar' },
      { name: 'ink-1', varName: '--ink-1' },
      { name: 'ink-3', varName: '--ink-3' },
      { name: 'ink-5', varName: '--ink-5' },
    ],
  },
  {
    group: 'Brand & Semantic',
    items: [
      { name: 'brand', varName: '--brand' },
      { name: 'brand-2', varName: '--brand-2' },
      { name: 'trust', varName: '--trust' },
      { name: 'decision', varName: '--decision' },
      { name: 'good', varName: '--good' },
      { name: 'warn', varName: '--warn' },
      { name: 'hot', varName: '--hot' },
      { name: 'premium', varName: '--premium' },
    ],
  },
  {
    group: 'Lead tiers',
    items: [
      { name: 'lead-hot', varName: '--lead-hot' },
      { name: 'lead-medium', varName: '--lead-medium' },
      { name: 'lead-cold', varName: '--lead-cold' },
    ],
  },
];

const Block: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={{ marginBottom: 40 }}>
    <h3
      style={{
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'var(--ink-4)',
        marginBottom: 16,
        paddingBottom: 8,
        borderBottom: '0.5px solid var(--line)',
      }}
    >
      {title}
    </h3>
    {children}
  </section>
);

const Row: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>{children}</div>
);

export const UiGallery: React.FC = () => (
  <div style={{ minHeight: '100vh', background: 'var(--surf-page)' }}>
    <TopBar avatarInitials="MC" notificationDot />
    <ColorRail />

    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 20px 80px' }}>
      <h1 style={{ fontSize: 28, marginBottom: 4 }}>CAR-IA · UI Gallery</h1>
      <p style={{ color: 'var(--ink-3)', marginBottom: 32 }}>
        Design system v3 — tokens, type, components. Single source of truth:{' '}
        <span className="mono">commodity/styles/theme.css</span> +{' '}
        <span className="mono">design-tokens.ts</span>.
      </p>

      <Block title="Color tokens">
        {SWATCHES.map((g) => (
          <div key={g.group} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 8 }}>
              {g.group}
            </div>
            <Row>
              {g.items.map((s) => (
                <div key={s.name} style={{ width: 96 }}>
                  <div
                    style={{
                      height: 56,
                      borderRadius: 10,
                      background: `var(${s.varName})`,
                      border: '0.5px solid var(--line)',
                    }}
                  />
                  <div
                    style={{ fontSize: 10, marginTop: 6, color: 'var(--ink-3)' }}
                    className="mono"
                  >
                    {s.name}
                  </div>
                </div>
              ))}
            </Row>
          </div>
        ))}
      </Block>

      <Block title="Typography">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-0.02em' }}>
            Inter · marketplace de confianza
          </div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Inter Semibold · títulos de tarjeta</div>
          <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>Inter Regular · cuerpo de texto</div>
          <div className="mono" style={{ fontSize: 26, fontWeight: 700 }}>
            $23,650 · 76,200 km
          </div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--ink-4)' }}>
            JetBrains Mono · precios y numéricos (tnum)
          </div>
        </div>
      </Block>

      <Block title="Buttons">
        <Row>
          <button className="btn btn-primary">Primary</button>
          <button className="btn btn-dark">Contactar</button>
          <button className="btn btn-secondary">Secondary</button>
          <button className="btn btn-outline">Outline</button>
          <button className="btn btn-ghost">Ghost</button>
          <button className="btn btn-success">Success</button>
          <button className="btn btn-error">Error</button>
          <button className="btn btn-primary btn-sm">Small</button>
          <button className="btn btn-primary btn-lg">Large</button>
          <button className="btn btn-primary" disabled>
            Disabled
          </button>
        </Row>
      </Block>

      <Block title="Chips — Trust / Decision / Status / Lead">
        <Row>
          <TrustChip state="verified" />
          <TrustChip state="pending" />
          <TrustChip state="failed" />
        </Row>
        <div style={{ height: 12 }} />
        <Row>
          <DecisionChip verdict="good-deal" />
          <DecisionChip verdict="fair-deal" />
          <DecisionChip verdict="overpriced" />
        </Row>
        <div style={{ height: 12 }} />
        <Row>
          <StatusChip status="draft" />
          <StatusChip status="active" />
          <StatusChip status="paused" />
          <StatusChip status="archived" />
          <StatusChip status="suspended" />
        </Row>
        <div style={{ height: 12 }} />
        <Row>
          <LeadChip tier="hot" />
          <LeadChip tier="medium" />
          <LeadChip tier="cold" />
          <PremiumBadge />
          <TasadorChip price="$25,170" />
          <PriceDropChip amount="RD$50k" days={12} />
        </Row>
      </Block>

      <Block title="Price block + section header + trust marks">
        <div
          style={{
            maxWidth: 380,
            background: 'var(--surf-card)',
            border: '0.5px solid var(--line)',
            borderRadius: 18,
            boxShadow: 'var(--sh-card)',
            overflow: 'hidden',
          }}
        >
          <PriceBlock
            usd="$23,650"
            secondary="RD$ 1,420,000"
            chips={
              <>
                <TasadorChip price="$25,170" />
                <DecisionChip verdict="good-deal" label="Excelente · 6% bajo" />
              </>
            }
          />
          <div style={{ padding: '12px 16px' }}>
            <SectionHeader
              title="Trust Signals"
              paren="confianza"
              variant="trust"
              trustSignals="4/4"
            />
            <TrustMarkGrid
              marks={[
                { key: 'tasado', label: 'Tasado', on: true },
                { key: 'inspected', label: 'Inspected', on: true },
                { key: 'carfax', label: 'Carfax', on: true },
                { key: 'verificado', label: 'Verificado', on: false },
              ]}
            />
          </div>
        </div>
      </Block>

      <Block title="VehicleCard">
        <div className="cia-feed" style={{ padding: 0 }}>
          <VehicleCard
            premium
            title="Mazda CX-5 Grand Touring"
            subtitle="2018 · 2.5 Skyactiv AWD"
            priceUsd="$23,650"
            priceSecondary="RD$ 1,420,000"
            imageUrl="https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800&h=600&fit=crop&q=80"
            photoCount={31}
            favorite
            priceChips={
              <>
                <TasadorChip price="$25,170" />
                <DecisionChip verdict="good-deal" label="Excelente · 6% bajo" />
              </>
            }
            spec={{
              year: '2018',
              mileageKm: '76,200',
              fuel: 'Gas',
              location: 'Piantini',
              distanceKm: '3.2km',
            }}
            trustMarks={[
              { key: 'tasado', label: 'Tasado', on: true },
              { key: 'inspected', label: 'Inspected', on: true },
              { key: 'carfax', label: 'Carfax', on: true },
              { key: 'verificado', label: 'Verificado', on: true },
            ]}
            trustSignals="4/4"
            onContact={() => {}}
            onMessage={() => {}}
          />
          <VehicleCard
            title="Honda Civic EX-L"
            subtitle="2020 · 1.5 Turbo"
            priceUsd="$19,900"
            priceSecondary="RD$ 1,194,000"
            imageUrl="https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=800&h=600&fit=crop&q=80"
            photoCount={18}
            priceChips={<DecisionChip verdict="fair-deal" />}
            spec={{
              year: '2020',
              mileageKm: '41,500',
              fuel: 'Gas',
              location: 'Naco',
              distanceKm: '5.1km',
            }}
            trustMarks={[
              { key: 'tasado', label: 'Tasado', on: true },
              { key: 'inspected', label: 'Inspected', on: false },
              { key: 'carfax', label: 'Carfax', on: true },
              { key: 'verificado', label: 'Verificado', on: true },
            ]}
            trustSignals="3/4"
            onContact={() => {}}
            onMessage={() => {}}
          />
        </div>
      </Block>
    </div>
  </div>
);

export default UiGallery;
